import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { apiClient } from '../api/client';

// ── Types ─────────────────────────────────────────────────────

interface UnlinkedRegistration {
  id: string;
  userId: string;
  userFullName: string;
  userEmail: string;
  eventId: string;
  eventName: string;
  chapterAffiliation: string | null;
  ticketNumber: number | null;
  registeredAt: string;
}

interface UnlinkedResponse {
  items: UnlinkedRegistration[];
  total: number;
}

interface GuestRecord {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  chapterAffiliation: string | null;
  registrationId: string | null;
  seat: Array<{
    compositeId: string;
    status: string;
    table: { tableNumber: number; tableName: string; zone?: { name: string } };
  }>;
}

interface UserRecord {
  id: string;
  fullName: string;
  email: string;
  role: string;
  isSynthetic: boolean;
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#f5f7f5',
    minHeight: '100vh',
  },
  title: {
    color: '#1a6b3c',
    fontSize: '1.75rem',
    margin: '0 0 1.5rem 0',
    fontWeight: 700,
  },
  filterContainer: {
    marginBottom: '1.5rem',
  },
  filterInput: {
    padding: '0.5rem 0.75rem',
    fontSize: '0.9rem',
    border: '1px solid #d0e8d6',
    borderRadius: '6px',
    width: '100%',
    maxWidth: '360px',
    outline: 'none',
  },
  section: {
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
    lineHeight: 1.4,
  },
  th: {
    padding: '0.6rem 0.75rem',
    textAlign: 'left' as const,
    backgroundColor: '#f0f7f2',
    borderBottom: '2px solid #1a6b3c',
    fontWeight: 600,
    color: '#0f2d1f',
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid #e8e8e8',
    color: '#333',
    verticalAlign: 'top' as const,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '0.95rem',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#888',
    fontSize: '0.9rem',
    fontStyle: 'italic' as const,
  },
  error: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#c0392b',
    fontSize: '0.9rem',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '1rem',
    padding: '0.75rem 0',
  },
  pageButton: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.85rem',
    border: '1px solid #d0e8d6',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#1a6b3c',
    cursor: 'pointer',
  },
  pageButtonActive: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.85rem',
    border: '1px solid #1a6b3c',
    borderRadius: '4px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    cursor: 'default',
    fontWeight: 600,
  },
  pageButtonDisabled: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.85rem',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#f5f5f5',
    color: '#aaa',
    cursor: 'not-allowed',
  },
  pageInfo: {
    fontSize: '0.85rem',
    color: '#666',
    margin: '0 0.5rem',
  },
  actionButton: {
    padding: '0.3rem 0.6rem',
    fontSize: '0.75rem',
    border: '1px solid #d0e8d6',
    borderRadius: '4px',
    backgroundColor: '#f0f7f2',
    color: '#1a6b3c',
    cursor: 'not-allowed',
    marginRight: '0.4rem',
    opacity: 0.6,
  },
  actionButtonEnabled: {
    padding: '0.3rem 0.6rem',
    fontSize: '0.75rem',
    border: '1px solid #1a6b3c',
    borderRadius: '4px',
    backgroundColor: '#f0f7f2',
    color: '#1a6b3c',
    cursor: 'pointer',
    marginRight: '0.4rem',
    opacity: 1,
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  modalTitle: {
    color: '#1a6b3c',
    fontSize: '1.2rem',
    fontWeight: 600,
    margin: '0 0 1rem 0',
  },
  modalLabel: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
    marginBottom: '0.3rem',
  },
  modalInput: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    fontSize: '0.9rem',
    border: '1px solid #d0e8d6',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  modalFieldGroup: {
    marginBottom: '1rem',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1.25rem',
  },
  modalCancelButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    border: '1px solid #d0e8d6',
    borderRadius: '6px',
    backgroundColor: '#fff',
    color: '#666',
    cursor: 'pointer',
  },
  modalSaveButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    border: '1px solid #1a6b3c',
    borderRadius: '6px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
  },
  modalSaveButtonDisabled: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    border: '1px solid #d0e8d6',
    borderRadius: '6px',
    backgroundColor: '#d0e8d6',
    color: '#888',
    cursor: 'not-allowed',
    fontWeight: 600,
  },
  modalError: {
    marginTop: '0.75rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fca5a5',
    borderRadius: '6px',
    color: '#b91c1c',
    fontSize: '0.8rem',
  },
  toast: {
    position: 'fixed' as const,
    bottom: '2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 2000,
  },
};

// ── Component ─────────────────────────────────────────────────

const PAGE_SIZE = 10;

export function RegistrationLinkingPage() {
  const [items, setItems] = useState<UnlinkedRegistration[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create & Link modal state
  const [createLinkReg, setCreateLinkReg] = useState<UnlinkedRegistration | null>(null);
  const [createLinkName, setCreateLinkName] = useState('');
  const [createLinkTicket, setCreateLinkTicket] = useState('');
  const [createLinkSaving, setCreateLinkSaving] = useState(false);
  const [createLinkError, setCreateLinkError] = useState<string | null>(null);

  // Link to Existing Guest modal state
  const [linkReg, setLinkReg] = useState<UnlinkedRegistration | null>(null);
  const [guestSearch, setGuestSearch] = useState('');
  const [guestResults, setGuestResults] = useState<GuestRecord[]>([]);
  const [guestSearchLoading, setGuestSearchLoading] = useState(false);
  const [selectedGuestId, setSelectedGuestId] = useState<string | null>(null);
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const guestSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reassign to Member modal state
  const [reassignReg, setReassignReg] = useState<UnlinkedRegistration | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<UserRecord[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [reassignSaving, setReassignSaving] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);
  const userSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Toast state
  const [toast, setToast] = useState<string | null>(null);

  // Debounce search input (300ms)
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }, []);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (guestSearchDebounceRef.current) {
        clearTimeout(guestSearchDebounceRef.current);
      }
      if (userSearchDebounceRef.current) {
        clearTimeout(userSearchDebounceRef.current);
      }
    };
  }, []);

  // Fetch unlinked registrations
  useEffect(() => {
    let cancelled = false;

    async function fetchUnlinked() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (debouncedSearch) params.set('search', debouncedSearch);
        params.set('page', String(page));
        params.set('pageSize', String(PAGE_SIZE));

        const data = await apiClient<UnlinkedResponse>(
          `/registration-linking/unlinked?${params.toString()}`
        );

        if (!cancelled) {
          setItems(data.items);
          setTotal(data.total);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message =
            err && typeof err === 'object' && 'message' in err
              ? (err as { message: string }).message
              : 'Failed to load unlinked registrations';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchUnlinked();

    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch]);

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Open Create & Link modal
  function openCreateLinkModal(reg: UnlinkedRegistration) {
    setCreateLinkReg(reg);
    setCreateLinkName(reg.userFullName);
    setCreateLinkTicket('');
    setCreateLinkError(null);
    setCreateLinkSaving(false);
  }

  // Close Create & Link modal
  function closeCreateLinkModal() {
    setCreateLinkReg(null);
    setCreateLinkName('');
    setCreateLinkTicket('');
    setCreateLinkError(null);
    setCreateLinkSaving(false);
  }

  // Open Link to Existing Guest modal
  function openLinkModal(reg: UnlinkedRegistration) {
    setLinkReg(reg);
    setGuestSearch('');
    setGuestResults([]);
    setSelectedGuestId(null);
    setLinkError(null);
    setLinkSaving(false);
    setGuestSearchLoading(false);
  }

  // Close Link to Existing Guest modal
  function closeLinkModal() {
    setLinkReg(null);
    setGuestSearch('');
    setGuestResults([]);
    setSelectedGuestId(null);
    setLinkError(null);
    setLinkSaving(false);
    setGuestSearchLoading(false);
    if (guestSearchDebounceRef.current) {
      clearTimeout(guestSearchDebounceRef.current);
    }
  }

  // Open Reassign to Member modal
  function openReassignModal(reg: UnlinkedRegistration) {
    setReassignReg(reg);
    setUserSearch('');
    setUserResults([]);
    setSelectedUserId(null);
    setReassignError(null);
    setReassignSaving(false);
    setUserSearchLoading(false);
  }

  // Close Reassign to Member modal
  function closeReassignModal() {
    setReassignReg(null);
    setUserSearch('');
    setUserResults([]);
    setSelectedUserId(null);
    setReassignError(null);
    setReassignSaving(false);
    setUserSearchLoading(false);
    if (userSearchDebounceRef.current) {
      clearTimeout(userSearchDebounceRef.current);
    }
  }

  // Search users by name (debounced)
  function handleUserSearchChange(value: string) {
    setUserSearch(value);
    setSelectedUserId(null);
    setReassignError(null);

    if (userSearchDebounceRef.current) {
      clearTimeout(userSearchDebounceRef.current);
    }

    if (!value.trim()) {
      setUserResults([]);
      setUserSearchLoading(false);
      return;
    }

    setUserSearchLoading(true);
    userSearchDebounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ search: value.trim() });
        const results = await apiClient<UserRecord[]>(`/users?${params.toString()}`);
        // Filter out the current owner of the registration being reassigned
        const filtered = (results || []).filter(
          (u) => u.id !== reassignReg?.userId && !u.isSynthetic
        );
        setUserResults(filtered);
      } catch {
        setUserResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
  }

  // Submit Reassign Registration
  async function handleReassign() {
    if (!reassignReg || !selectedUserId) return;

    setReassignSaving(true);
    setReassignError(null);

    try {
      await apiClient('/registration-linking/reassign', {
        method: 'POST',
        body: { registrationId: reassignReg.id, targetUserId: selectedUserId },
      });

      // Success: remove from list, show toast, close modal
      setItems((prev) => prev.filter((r) => r.id !== reassignReg.id));
      setTotal((prev) => prev - 1);
      setToast('Registration reassigned successfully');
      closeReassignModal();
    } catch (err: unknown) {
      const errorObj = err as { statusCode?: number; message?: string };
      setReassignError(errorObj.message || 'Failed to reassign registration.');
    } finally {
      setReassignSaving(false);
    }
  }

  // Search guests by name (debounced)
  function handleGuestSearchChange(value: string) {
    setGuestSearch(value);
    setSelectedGuestId(null);
    setLinkError(null);

    if (guestSearchDebounceRef.current) {
      clearTimeout(guestSearchDebounceRef.current);
    }

    if (!value.trim()) {
      setGuestResults([]);
      setGuestSearchLoading(false);
      return;
    }

    setGuestSearchLoading(true);
    guestSearchDebounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ query: value.trim() });
        const results = await apiClient<{ guests: GuestRecord[]; page: number; totalPages: number; totalGuests: number }>(
          `/table-management/guests?${params.toString()}`
        );
        setGuestResults(results.guests || []);
      } catch {
        setGuestResults([]);
      } finally {
        setGuestSearchLoading(false);
      }
    }, 300);
  }

  // Submit Link to Existing Guest
  async function handleLinkGuest() {
    if (!linkReg || !selectedGuestId) return;

    setLinkSaving(true);
    setLinkError(null);

    try {
      await apiClient('/registration-linking/link', {
        method: 'POST',
        body: { guestId: selectedGuestId, registrationId: linkReg.id },
      });

      // Success: remove from list, show toast, close modal
      setItems((prev) => prev.filter((r) => r.id !== linkReg.id));
      setTotal((prev) => prev - 1);
      setToast(`Registration linked to guest successfully`);
      closeLinkModal();
    } catch (err: unknown) {
      const errorObj = err as { statusCode?: number; message?: string };
      if (errorObj.statusCode === 409) {
        setLinkError(errorObj.message || 'This guest or registration is already linked.');
      } else {
        setLinkError(errorObj.message || 'Failed to link guest.');
      }
    } finally {
      setLinkSaving(false);
    }
  }

  // Submit Create & Link
  async function handleCreateAndLink(e: FormEvent) {
    e.preventDefault();
    if (!createLinkReg || !createLinkName.trim()) return;

    setCreateLinkSaving(true);
    setCreateLinkError(null);

    try {
      const body: { registrationId: string; fullName: string; ticketNumber?: number } = {
        registrationId: createLinkReg.id,
        fullName: createLinkName.trim(),
      };
      if (createLinkTicket.trim()) {
        body.ticketNumber = Number(createLinkTicket.trim());
      }

      await apiClient('/registration-linking/create-and-link', {
        method: 'POST',
        body,
      });

      // Success: remove from list, show toast, close modal
      setItems((prev) => prev.filter((r) => r.id !== createLinkReg.id));
      setTotal((prev) => prev - 1);
      setToast(`Guest created and linked to ${createLinkReg.userFullName}`);
      closeCreateLinkModal();
    } catch (err: unknown) {
      const errorObj = err as { statusCode?: number; message?: string };
      if (errorObj.statusCode === 409) {
        setCreateLinkError(errorObj.message || 'This registration is already linked to another guest.');
      } else {
        setCreateLinkError(errorObj.message || 'Failed to create and link guest.');
      }
    } finally {
      setCreateLinkSaving(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function renderPagination() {
    if (total <= PAGE_SIZE) return null;

    return (
      <div style={styles.pagination}>
        <button
          style={page <= 1 ? styles.pageButtonDisabled : styles.pageButton}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
        >
          ← Prev
        </button>

        <span style={styles.pageInfo}>
          Page {page} of {totalPages}
        </span>

        <button
          style={page >= totalPages ? styles.pageButtonDisabled : styles.pageButton}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
        >
          Next →
        </button>
      </div>
    );
  }

  function renderContent() {
    if (loading) {
      return <div style={styles.loading}>Loading unlinked registrations...</div>;
    }

    if (error) {
      return <div style={styles.error}>{error}</div>;
    }

    if (items.length === 0) {
      return (
        <div style={styles.emptyState}>
          {debouncedSearch
            ? 'No unlinked registrations match your search'
            : 'All registrations are linked — nothing to do here'}
        </div>
      );
    }

    return (
      <>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Event</th>
              <th style={styles.th}>Chapter</th>
              <th style={styles.th}>Ticket #</th>
              <th style={styles.th}>Registered</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((reg) => (
              <tr key={reg.id}>
                <td style={styles.td}>{reg.userFullName}</td>
                <td style={styles.td}>{reg.userEmail}</td>
                <td style={styles.td}>{reg.eventName}</td>
                <td style={styles.td}>{reg.chapterAffiliation || '—'}</td>
                <td style={styles.td}>{reg.ticketNumber ?? '—'}</td>
                <td style={styles.td}>
                  {new Date(reg.registeredAt).toLocaleDateString()}
                </td>
                <td style={styles.td}>
                  <button
                    style={styles.actionButtonEnabled}
                    onClick={() => openLinkModal(reg)}
                  >
                    Link to Existing Guest
                  </button>
                  <button
                    style={styles.actionButtonEnabled}
                    onClick={() => openCreateLinkModal(reg)}
                  >
                    Create &amp; Link
                  </button>
                  <button
                    style={styles.actionButtonEnabled}
                    onClick={() => openReassignModal(reg)}
                  >
                    Reassign to Member
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {renderPagination()}
      </>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Registration–Guest Linking</h1>

      <div style={styles.filterContainer}>
        <input
          type="text"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          style={styles.filterInput}
          aria-label="Search unlinked registrations by name"
        />
      </div>

      <div style={styles.section}>
        {renderContent()}
      </div>

      {/* Create & Link Modal */}
      {createLinkReg && (
        <div style={styles.modalOverlay} onClick={closeCreateLinkModal}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Create &amp; Link Guest</h2>
            <form onSubmit={handleCreateAndLink}>
              <div style={styles.modalFieldGroup}>
                <label style={styles.modalLabel} htmlFor="createLinkName">
                  Full Name
                </label>
                <input
                  id="createLinkName"
                  type="text"
                  style={styles.modalInput}
                  value={createLinkName}
                  onChange={(e) => setCreateLinkName(e.target.value)}
                  required
                />
              </div>
              <div style={styles.modalFieldGroup}>
                <label style={styles.modalLabel} htmlFor="createLinkTicket">
                  Ticket Number (optional)
                </label>
                <input
                  id="createLinkTicket"
                  type="number"
                  style={styles.modalInput}
                  value={createLinkTicket}
                  onChange={(e) => setCreateLinkTicket(e.target.value)}
                  placeholder="e.g. 1042"
                />
              </div>

              {createLinkError && (
                <div style={styles.modalError}>{createLinkError}</div>
              )}

              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.modalCancelButton}
                  onClick={closeCreateLinkModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={
                    createLinkSaving || !createLinkName.trim()
                      ? styles.modalSaveButtonDisabled
                      : styles.modalSaveButton
                  }
                  disabled={createLinkSaving || !createLinkName.trim()}
                >
                  {createLinkSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Link to Existing Guest Modal */}
      {linkReg && (
        <div style={styles.modalOverlay} onClick={closeLinkModal}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Link to Existing Guest</h2>
            <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 1rem 0' }}>
              Linking registration for <strong>{linkReg.userFullName}</strong>
            </p>

            <div style={styles.modalFieldGroup}>
              <label style={styles.modalLabel}>Search guests by name</label>
              <input
                type="text"
                placeholder="Type a name to search..."
                value={guestSearch}
                onChange={(e) => handleGuestSearchChange(e.target.value)}
                style={styles.modalInput}
                autoFocus
              />
            </div>

            {/* Guest results list */}
            {guestSearchLoading && (
              <div style={{ padding: '1rem', textAlign: 'center' as const, color: '#666', fontSize: '0.85rem' }}>
                Searching...
              </div>
            )}

            {!guestSearchLoading && guestSearch.trim() && guestResults.length === 0 && (
              <div style={{ padding: '1rem', textAlign: 'center' as const, color: '#888', fontSize: '0.85rem', fontStyle: 'italic' as const }}>
                No guests found matching &ldquo;{guestSearch}&rdquo;
              </div>
            )}

            {!guestSearchLoading && guestResults.length > 0 && (
              <div style={{ maxHeight: '220px', overflowY: 'auto' as const, border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '0.5rem' }}>
                {guestResults.map((guest) => (
                  <div
                    key={guest.id}
                    onClick={() => setSelectedGuestId(guest.id)}
                    style={{
                      padding: '0.6rem 0.75rem',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      backgroundColor: selectedGuestId === guest.id ? '#1a6b3c' : '#fff',
                      color: selectedGuestId === guest.id ? '#fff' : '#333',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {guest.fullName}
                      {guest.registrationId && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: selectedGuestId === guest.id ? '#fca5a5' : '#b91c1c' }}>
                          (already linked)
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: selectedGuestId === guest.id ? '#d0e8d6' : '#666', marginTop: '0.15rem' }}>
                      {guest.seat && guest.seat.length > 0
                        ? `🪑 Table ${guest.seat[0].table.tableNumber} (${guest.seat[0].table.tableName})${guest.seat[0].table.zone ? ` · ${guest.seat[0].table.zone.name}` : ''}`
                        : '⚠️ No seat assigned'}
                      {guest.ticketNumber ? ` · Ticket ${guest.ticketNumber}` : ''}
                      {guest.chapterAffiliation ? ` · ${guest.chapterAffiliation}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {linkError && (
              <div style={styles.modalError}>{linkError}</div>
            )}

            <div style={styles.modalActions}>
              <button style={styles.modalCancelButton} onClick={closeLinkModal}>
                Cancel
              </button>
              <button
                style={!selectedGuestId || linkSaving ? styles.modalSaveButtonDisabled : styles.modalSaveButton}
                disabled={!selectedGuestId || linkSaving}
                onClick={handleLinkGuest}
              >
                {linkSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign to Member Modal */}
      {reassignReg && (
        <div style={styles.modalOverlay} onClick={closeReassignModal}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Reassign to Member</h2>
            <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 1rem 0' }}>
              Move registration from <strong>{reassignReg.userFullName}</strong> to a real GA member account.
              The synthetic account will be deactivated after reassignment.
            </p>

            <div style={styles.modalFieldGroup}>
              <label style={styles.modalLabel}>Search members by name or email</label>
              <input
                type="text"
                placeholder="Type a name or email to search..."
                value={userSearch}
                onChange={(e) => handleUserSearchChange(e.target.value)}
                style={styles.modalInput}
                autoFocus
                aria-label="Search users for reassignment"
              />
            </div>

            {/* User results list */}
            {userSearchLoading && (
              <div style={{ padding: '1rem', textAlign: 'center' as const, color: '#666', fontSize: '0.85rem' }}>
                Searching...
              </div>
            )}

            {!userSearchLoading && userSearch.trim() && userResults.length === 0 && (
              <div style={{ padding: '1rem', textAlign: 'center' as const, color: '#888', fontSize: '0.85rem', fontStyle: 'italic' as const }}>
                No members found matching &ldquo;{userSearch}&rdquo;
              </div>
            )}

            {!userSearchLoading && userResults.length > 0 && (
              <div style={{ maxHeight: '220px', overflowY: 'auto' as const, border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '0.5rem' }}>
                {userResults.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => setSelectedUserId(user.id)}
                    style={{
                      padding: '0.6rem 0.75rem',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      backgroundColor: selectedUserId === user.id ? '#1a6b3c' : '#fff',
                      color: selectedUserId === user.id ? '#fff' : '#333',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {user.fullName}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: selectedUserId === user.id ? '#d0e8d6' : '#666', marginTop: '0.15rem' }}>
                      {user.email} · {user.role}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {reassignError && (
              <div style={styles.modalError}>{reassignError}</div>
            )}

            <div style={styles.modalActions}>
              <button style={styles.modalCancelButton} onClick={closeReassignModal}>
                Cancel
              </button>
              <button
                style={!selectedUserId || reassignSaving ? styles.modalSaveButtonDisabled : styles.modalSaveButton}
                disabled={!selectedUserId || reassignSaving}
                onClick={handleReassign}
              >
                {reassignSaving ? 'Reassigning...' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {toast && <div style={styles.toast}>{toast}</div>}
    </div>
  );
}
